import {
  ClipboardCheck, FileStack, FlaskConical, ShieldCheck, Factory, Recycle,
  FileSpreadsheet, Leaf, Warehouse, Beaker, Pill, Wrench, Package,
  Zap, UtensilsCrossed, Atom, FileCheck2, Database, ScrollText
} from "lucide-react";

export const COMPANY = {
  name: "Wasserlab",
  brand: "Wasserlab — EHS Remote",
  platform: "EHS Remote",
  tagline: "U.S. Environmental Compliance Support, Delivered Remotely.",
  positioning: "The U.S.-focused remote EHS & environmental services platform by Wasserlab.",
  email: "wasserlabpvtltd@gmail.com",
  usPhone: process.env.REACT_APP_US_PHONE || "",
  usServices: "U.S. Client Services",
  indiaAddress: "3-297, Shree Gururaksha, Mudradi, Hebri Tq, Udupi, Pin-576112",
};

export const SERVICES = [
  {
    slug: "tier-ii-reporting-support",
    icon: ClipboardCheck,
    name: "Tier II Reporting Support",
    short: "End-to-end preparation support for EPCRA Tier II chemical inventory reporting.",
    featured: true,
    intro: "Structured preparation support for annual Tier II (EPCRA Section 312) chemical inventory reports for U.S. industrial facilities.",
    body: "Under the Emergency Planning and Community Right-to-Know Act (EPCRA), facilities that store hazardous chemicals above threshold quantities must file an annual Tier II report with their State Emergency Response Commission (SERC), Local Emergency Planning Committee (LEPC) and local fire department. Wasserlab provides the technical groundwork — data collection, SDS review, quantity calculations and documentation — so your EHS team can review and submit with confidence.",
    deliverables: [
      "Chemical inventory data collection",
      "Safety Data Sheet (SDS) review",
      "Chemical identification",
      "CAS number verification",
      "Maximum quantity calculations",
      "Average quantity calculations",
      "Storage location organization",
      "Storage method information",
      "Hazard information organization",
      "State-specific reporting preparation",
      "Tier2 Submit data preparation/support",
      "Annual reporting updates",
      "Documentation and audit preparation",
    ],
    keywords: "Tier II reporting services, Tier II reporting consultant, Tier II compliance services, hazardous chemical inventory reporting",
  },
  {
    slug: "epcra-311-312-support",
    icon: FileCheck2,
    name: "EPCRA 311/312 Support",
    short: "SDS submission (311) and annual inventory (312) preparation support.",
    intro: "Preparation support for EPCRA Sections 311 (SDS/MSDS submission) and 312 (annual inventory) obligations.",
    body: "EPCRA Section 311 requires facilities to submit Safety Data Sheets (or a list of hazardous chemicals) to local authorities, while Section 312 covers the annual Tier II inventory. Wasserlab helps organize your chemical lists, map hazard categories and prepare the documentation needed to meet both requirements.",
    deliverables: [
      "SDS/chemical list preparation (311)",
      "Hazard category classification",
      "Annual inventory preparation (312)",
      "Threshold planning quantity review",
      "Recordkeeping organization",
      "Submission-ready data packages",
    ],
    keywords: "EPCRA reporting services, EPCRA 312 consultant, EPCRA 311 support",
  },
  {
    slug: "chemical-inventory-management",
    icon: Database,
    name: "Chemical Inventory Management",
    short: "Centralized, audit-ready chemical inventory data management.",
    intro: "Build and maintain a structured, audit-ready chemical inventory for your facility.",
    body: "A clean, current chemical inventory is the foundation of every compliance program. Wasserlab structures your chemical data — quantities, locations, hazards and SDS linkage — into an organized, reusable dataset that streamlines Tier II, TRI and internal EHS reporting.",
    deliverables: [
      "Chemical master list creation",
      "Quantity and location tracking structure",
      "SDS-to-chemical linkage",
      "Hazard classification mapping",
      "Data validation and cleanup",
      "Recurring inventory updates",
    ],
    keywords: "chemical inventory management, hazardous chemical inventory reporting",
  },
  {
    slug: "sds-management",
    icon: FileStack,
    name: "SDS Management",
    short: "Safety Data Sheet collection, organization and gap identification.",
    intro: "Centralize and maintain your Safety Data Sheet library.",
    body: "Missing or outdated SDS documents create compliance risk. Wasserlab helps collect, organize and review your SDS library, identifies gaps and links each sheet to your chemical inventory for fast retrieval during audits and reporting.",
    deliverables: [
      "SDS collection and organization",
      "SDS gap identification",
      "Hazard data extraction",
      "SDS-to-inventory linkage",
      "Version and currency tracking",
      "Searchable SDS library structure",
    ],
    keywords: "SDS management, safety data sheet management",
  },
  {
    slug: "tri-epcra-313-support",
    icon: FileSpreadsheet,
    name: "TRI / EPCRA 313 Support",
    short: "Toxics Release Inventory (Form R / Form A) preparation support.",
    intro: "Preparation support for Toxics Release Inventory (TRI) reporting under EPCRA Section 313.",
    body: "Facilities meeting TRI thresholds must report releases and other waste management of listed toxic chemicals. Wasserlab supports the data gathering, threshold analysis and release calculations needed to prepare Form R and Form A submissions for your review.",
    deliverables: [
      "TRI applicability and threshold analysis",
      "Release and waste data compilation",
      "Chemical activity threshold review",
      "Form R / Form A data preparation",
      "Supporting documentation",
      "Annual TRI update support",
    ],
    keywords: "TRI reporting, EPCRA 313 support, toxics release inventory",
  },
  {
    slug: "environmental-compliance-support",
    icon: ShieldCheck,
    name: "Environmental Compliance Support",
    short: "Ongoing remote support across environmental reporting programs.",
    intro: "Broad remote environmental compliance support tailored to your facility's programs.",
    body: "Beyond chemical inventory, facilities juggle a range of environmental obligations. Wasserlab provides structured, technology-enabled support across recurring environmental reporting and documentation tasks, freeing your internal team to focus on operations and final decision-making.",
    deliverables: [
      "Compliance calendar organization",
      "Recurring report preparation",
      "Regulatory data compilation",
      "Documentation and recordkeeping",
      "Audit preparation support",
      "Multi-facility coordination",
    ],
    keywords: "environmental compliance consultant, remote environmental compliance support",
  },
  {
    slug: "remote-ehs-documentation",
    icon: ScrollText,
    name: "Remote EHS Documentation",
    short: "Structured EHS documentation and recordkeeping support.",
    intro: "Remote support for Environmental, Health & Safety documentation and recordkeeping.",
    body: "Well-maintained EHS documentation is essential for audits, inspections and internal governance. Wasserlab helps build and maintain structured documentation — templates, logs, registers and records — delivered remotely by experienced environmental professionals.",
    deliverables: [
      "EHS document template creation",
      "Recordkeeping and log maintenance",
      "Compliance register organization",
      "Document control structure",
      "Audit-ready documentation",
      "Periodic documentation reviews",
    ],
    keywords: "remote EHS support, EHS documentation",
  },
  {
    slug: "esg-sustainability-support",
    icon: Leaf,
    name: "ESG & Sustainability Support",
    short: "Data support for ESG and sustainability reporting initiatives.",
    intro: "Structured data and documentation support for ESG and sustainability programs.",
    body: "As stakeholders demand more transparency, structured environmental data becomes a strategic asset. Wasserlab supports the data collection, organization and documentation that underpin sustainability metrics and ESG reporting frameworks.",
    deliverables: [
      "Environmental data collection",
      "Emissions and resource data organization",
      "Sustainability metric preparation",
      "ESG documentation support",
      "Framework-aligned data structuring",
      "Recurring reporting support",
    ],
    keywords: "ESG support, sustainability reporting support",
  },
];

export const INDUSTRIES = [
  { slug: "manufacturing", icon: Factory, name: "Manufacturing", short: "Chemical inventory and Tier II support for manufacturing plants.",
    body: "Manufacturing facilities frequently store fuels, solvents, coatings and process chemicals above EPCRA thresholds. Wasserlab helps manufacturers maintain accurate inventories and prepare Tier II and related reports across single and multi-plant operations." },
  { slug: "chemicals", icon: FlaskConical, name: "Chemicals", short: "Rigorous inventory and hazard data support for chemical operations.",
    body: "Chemical producers and blenders handle complex, high-hazard inventories. Wasserlab brings structured workflows and multi-level review to chemical identification, hazard classification and quantity calculations for Tier II and TRI reporting." },
  { slug: "warehousing-distribution", icon: Warehouse, name: "Warehousing & Distribution", short: "Inventory support for distribution centers and 3PL facilities.",
    body: "Warehouses and distribution centers often store large, changing quantities of hazardous products. Wasserlab helps track and organize stored chemical data so Tier II thresholds are monitored and reporting stays current." },
  { slug: "food-beverage", icon: UtensilsCrossed, name: "Food & Beverage", short: "Ammonia, sanitizers and refrigerants inventory support.",
    body: "Food and beverage facilities commonly store anhydrous ammonia, cleaning agents and refrigerants that trigger EPCRA reporting. Wasserlab supports accurate inventory and Tier II preparation for these operations." },
  { slug: "pharmaceuticals", icon: Pill, name: "Pharmaceuticals", short: "Compliance support for pharmaceutical and life-science sites.",
    body: "Pharmaceutical manufacturers manage diverse solvents, reagents and controlled materials. Wasserlab provides careful chemical data management and reporting preparation aligned to the sector's documentation standards." },
  { slug: "laboratories", icon: Beaker, name: "Laboratories", short: "Small-quantity, high-variety chemical inventory support.",
    body: "Labs handle a wide variety of chemicals in smaller quantities, making inventory tracking and aggregation challenging. Wasserlab structures lab chemical data for accurate threshold analysis and reporting." },
  { slug: "metal-fabrication", icon: Wrench, name: "Metal & Fabrication", short: "Support for machining, plating and fabrication facilities.",
    body: "Metal and fabrication shops use fuels, gases, acids and coating chemicals subject to EPCRA. Wasserlab helps organize these inventories and prepare compliant reporting documentation." },
  { slug: "plastics", icon: Package, name: "Plastics", short: "Resin, additive and process chemical inventory support.",
    body: "Plastics processors store resins, additives and process chemicals that may exceed reporting thresholds. Wasserlab supports accurate inventory management and Tier II preparation." },
  { slug: "energy", icon: Zap, name: "Energy", short: "Support for energy, power and fuel-handling facilities.",
    body: "Energy facilities handle fuels, oils and treatment chemicals in significant quantities. Wasserlab provides structured inventory and reporting support tailored to energy-sector operations." },
];

export const ARTICLES = [
  {
    slug: "what-is-tier-ii-reporting",
    title: "What Is Tier II Reporting?",
    category: "Tier II Guides",
    excerpt: "A plain-language overview of the EPCRA Tier II chemical inventory report — what it is, why it exists, and what facilities need to know.",
    read: "6 min read",
    sections: [
      { h: "The basics", p: "Tier II reporting is an annual requirement under the Emergency Planning and Community Right-to-Know Act (EPCRA), specifically Section 312. Facilities that store hazardous chemicals at or above certain threshold quantities must submit an inventory report describing what they store, how much, where, and the associated hazards." },
      { h: "Why it exists", p: "EPCRA was enacted so that emergency responders and communities have information about hazardous chemicals stored nearby. The Tier II report gives fire departments, Local Emergency Planning Committees (LEPCs) and State Emergency Response Commissions (SERCs) the data they need to plan for and respond to chemical emergencies." },
      { h: "What a report contains", p: "A typical Tier II submission includes chemical identity (often with CAS numbers), maximum and average daily amounts, storage locations, storage types and conditions, and hazard classifications. Many states use the Tier2 Submit software or their own state portal." },
      { h: "When it's due", p: "Tier II reports are generally due by March 1 each year, covering the previous calendar year. State-specific deadlines, fees and portals vary, so facilities should confirm requirements with their state agency." },
      { h: "How Wasserlab helps", p: "Wasserlab provides the technical preparation — collecting chemical data, reviewing SDS, verifying CAS numbers and calculating quantities — so your EHS team can review and submit accurately. Final determinations and submissions remain your responsibility." },
    ],
  },
  {
    slug: "who-needs-to-file-a-tier-ii-report",
    title: "Who Needs to File a Tier II Report?",
    category: "Tier II Guides",
    excerpt: "Understand the threshold quantities and facility types that trigger an EPCRA Tier II filing obligation.",
    read: "5 min read",
    sections: [
      { h: "Threshold quantities", p: "In general, facilities must report hazardous chemicals for which they are required to maintain a Safety Data Sheet under OSHA and that are present at or above 10,000 pounds at any time during the year. Extremely Hazardous Substances (EHSs) have much lower thresholds — 500 pounds or the Threshold Planning Quantity (TPQ), whichever is less." },
      { h: "Common regulated chemicals", p: "Fuels such as diesel and gasoline, sulfuric acid in batteries, anhydrous ammonia, propane, solvents and many process chemicals commonly exceed thresholds. Even seemingly routine materials can trigger reporting when stored in bulk." },
      { h: "Facility types", p: "Manufacturing plants, warehouses, chemical operations, food and beverage facilities, labs, energy and many other industrial sites may be subject to Tier II reporting depending on what and how much they store." },
      { h: "How to be sure", p: "The only way to know is to inventory your chemicals and compare quantities against applicable thresholds. Wasserlab's assessment process helps you evaluate readiness — but final applicability determinations rest with your organization and applicable regulators." },
    ],
  },
  {
    slug: "tier-ii-chemical-inventory-checklist",
    title: "Tier II Chemical Inventory Checklist",
    category: "Checklists",
    excerpt: "A practical checklist to organize your facility's chemical inventory before Tier II reporting season.",
    read: "4 min read",
    sections: [
      { h: "Gather your chemicals", p: "Walk every storage area and list all hazardous chemicals on site, including those in tanks, drums, totes, cylinders and smaller containers. Don't overlook maintenance shops, labs and outdoor storage." },
      { h: "Collect SDS", p: "Ensure a current Safety Data Sheet exists for every hazardous chemical. Note any gaps or outdated sheets to resolve before reporting." },
      { h: "Record quantities", p: "Capture maximum and average daily amounts for each chemical over the reporting year. Consistent units and clear assumptions matter for accurate calculations." },
      { h: "Map storage details", p: "Document storage locations, container types, storage conditions (pressure, temperature) and hazard classes for each chemical." },
      { h: "Verify identifiers", p: "Confirm chemical names and CAS numbers, and reconcile mixtures against their components where required." },
      { h: "Review and submit", p: "Have qualified personnel review the compiled data before submitting through your state's portal or Tier2 Submit. Retain documentation for audits." },
    ],
  },
  {
    slug: "epcra-311-vs-312",
    title: "EPCRA 311 vs 312",
    category: "EPCRA Guides",
    excerpt: "The difference between EPCRA Section 311 (SDS submission) and Section 312 (annual inventory) explained clearly.",
    read: "5 min read",
    sections: [
      { h: "Section 311 — SDS submission", p: "Section 311 requires facilities to submit Safety Data Sheets — or a list of hazardous chemicals grouped by hazard category — to the LEPC, SERC and local fire department. This is typically a one-time submission that is updated when new hazardous chemicals are introduced." },
      { h: "Section 312 — annual inventory", p: "Section 312 is the annual Tier II inventory report describing quantities, locations and hazards of stored chemicals. Unlike 311, it must be filed every year by the applicable deadline." },
      { h: "How they relate", p: "Both sections stem from the same EPCRA hazardous chemical thresholds. In practice, 311 establishes what hazardous chemicals are present and 312 quantifies and locates them annually." },
      { h: "Staying compliant", p: "Facilities need processes for both the initial and ongoing 311 obligations and the recurring 312 filing. Wasserlab helps organize the underlying chemical data that supports both." },
    ],
  },
  {
    slug: "common-tier-ii-reporting-mistakes",
    title: "Common Tier II Reporting Mistakes",
    category: "Tier II Guides",
    excerpt: "Avoid the errors that most often lead to inaccurate Tier II reports and compliance risk.",
    read: "5 min read",
    sections: [
      { h: "Overlooking chemicals", p: "Facilities frequently miss chemicals stored in secondary areas, maintenance shops or outdoor tanks. A complete site walk-through is essential." },
      { h: "Incorrect quantity calculations", p: "Confusing maximum with average amounts, using inconsistent units, or failing to account for peak inventory can produce inaccurate reports." },
      { h: "Outdated or missing SDS", p: "Reporting without current SDS leads to wrong hazard classifications. Maintaining an up-to-date SDS library prevents this." },
      { h: "Missing EHS thresholds", p: "Extremely Hazardous Substances have far lower thresholds. Applying the generic 10,000-pound threshold to an EHS is a common and serious error." },
      { h: "Ignoring state specifics", p: "States differ on portals, fees and additional data fields. Preparing to federal-only requirements can result in rejected or incomplete filings." },
    ],
  },
  {
    slug: "how-to-prepare-your-facility-for-tier-ii-reporting",
    title: "How to Prepare Your Facility for Tier II Reporting",
    category: "Tier II Guides",
    excerpt: "A step-by-step approach to getting your facility ready for accurate, on-time Tier II reporting.",
    read: "6 min read",
    sections: [
      { h: "Start early", p: "Begin compiling data well before the March 1 deadline. Early preparation leaves time to resolve SDS gaps and quantity questions." },
      { h: "Build a master inventory", p: "Create a single, structured chemical inventory that serves as the source of truth for Tier II and other reporting programs." },
      { h: "Assign ownership", p: "Designate responsible personnel for data collection, review and submission, and document the process for consistency year over year." },
      { h: "Leverage support", p: "Technical preparation is time-consuming. Structured, technology-enabled support — like Wasserlab's — reduces the burden on your internal team while keeping final review in your hands." },
      { h: "Document everything", p: "Retain the data, calculations and SDS used for each report to support audits and future filings." },
    ],
  },
  {
    slug: "chemical-inventory-management-guide",
    title: "Chemical Inventory Management Guide",
    category: "Chemical Inventory",
    excerpt: "How to build and maintain a reliable chemical inventory that powers all your environmental reporting.",
    read: "7 min read",
    sections: [
      { h: "Why inventory management matters", p: "A single, accurate chemical inventory underpins Tier II, TRI, SDS management and internal EHS programs. Fragmented data leads to duplicated effort and reporting errors." },
      { h: "Core data fields", p: "Capture chemical name, CAS number, hazard classes, quantities (max and average), storage location, container type and SDS linkage for every material." },
      { h: "Keeping it current", p: "Establish a cadence for updates as chemicals are added, removed or change in quantity. Periodic reconciliation against purchasing and usage data improves accuracy." },
      { h: "From data to reporting", p: "A well-structured inventory can be filtered and exported to support multiple reporting obligations, dramatically reducing preparation time." },
      { h: "How Wasserlab supports you", p: "Wasserlab builds and maintains structured inventories with multi-level quality review and secure document handling, delivered remotely by environmental professionals." },
    ],
  },
  {
    slug: "how-environmental-consultants-can-outsource-tier-ii-reporting",
    title: "How Environmental Consultants Can Outsource Tier II Reporting",
    category: "For Consultants",
    excerpt: "How U.S. environmental and EHS consulting firms can add reporting capacity by outsourcing Tier II preparation while keeping the client relationship.",
    read: "6 min read",
    sections: [
      { h: "The capacity problem", p: "Reporting season concentrates a large volume of Tier II work into a short window. Hiring full-time staff for a seasonal peak is expensive and hard to justify. Outsourcing the preparation lets firms scale capacity up and down as demand changes." },
      { h: "What stays with your firm", p: "You keep the client relationship, scope decisions and final review. A support partner like Wasserlab handles the technical preparation behind the scenes — data structuring, SDS review, chemical identification and quantity calculations." },
      { h: "White-label delivery", p: "Deliverables can be prepared to your firm's standards and presented under your brand where appropriate, so the experience to your client remains seamless." },
      { h: "Quality and confidentiality", p: "Look for a partner with a structured, multi-step review process and clear confidentiality practices, including NDA availability and access-controlled document handling." },
      { h: "Getting started", p: "Start with a small, scoped engagement to validate the workflow before committing to a full season. Requirements vary by state and facility — your firm remains responsible for final determinations." },
    ],
  },
  {
    slug: "preparing-for-the-2027-tier-ii-reporting-changes",
    title: "Preparing for the 2027 Tier II Reporting Changes",
    category: "Tier II Guides",
    excerpt: "Reporting requirements evolve over time. A practical, no-hype look at how facilities can stay ready as Tier II expectations change heading toward the 2027 reporting cycles.",
    read: "6 min read",
    sections: [
      { h: "Requirements change over time", p: "EPCRA Tier II requirements — including data fields, state portals and submission procedures — are periodically updated by the EPA and state agencies. Facilities should treat compliance as an ongoing process rather than a one-time task, and confirm the current requirements each reporting year." },
      { h: "Why preparation matters early", p: "When new data elements or formats are introduced, facilities with a clean, well-structured chemical inventory adapt far more easily than those starting from scratch. Building good data habits now reduces future rework." },
      { h: "Practical steps to stay ready", p: "Maintain a current chemical master list, keep SDS organized and up to date, document quantities and storage details consistently, and reconcile your inventory regularly. These fundamentals carry over regardless of specific rule changes." },
      { h: "Confirm with authoritative sources", p: "For the most accurate and current requirements, consult the EPA's EPCRA resources and your State Emergency Response Commission (SERC). Deadlines, fees and portals vary by state and can change. This article is general guidance and not a statement of specific future requirements." },
      { h: "How Wasserlab helps", p: "We help facilities and consultants keep inventories structured and reporting-ready year over year, so you can adapt to changes with less disruption. Final determinations and submissions remain the client's responsibility." },
    ],
  },
];

export const WHY_POINTS = [
  { icon: FlaskConical, title: "Environmental engineering expertise", text: "Reports are prepared by experienced environmental professionals who understand regulatory frameworks." },
  { icon: ClipboardCheck, title: "Structured compliance workflows", text: "Repeatable, documented processes bring consistency and rigor to every engagement." },
  { icon: Database, title: "Technology-enabled document processing", text: "Modern tooling streamlines SDS review, data extraction and inventory management." },
  { icon: ShieldCheck, title: "Multi-level quality review", text: "Layered review reduces errors before data reaches your team for final approval." },
  { icon: FileStack, title: "Secure document handling", text: "Uploaded SDS and facility information are handled with security-first practices." },
  { icon: Recycle, title: "Recurring compliance support", text: "Annual and ongoing support keeps your reporting current year after year." },
  { icon: Atom, title: "Remote delivery", text: "A specialized global team delivers support efficiently, wherever your facilities are." },
  { icon: Leaf, title: "Cost-efficient global delivery", text: "A remote delivery model reduces the workload and cost on your internal EHS team." },
];

export const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia",
  "Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts",
  "Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey",
  "New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island",
  "South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming"
];

export const FACILITY_TYPES = [
  "Manufacturing","Chemicals","Warehousing & Distribution","Food & Beverage","Pharmaceuticals",
  "Laboratories","Metal & Fabrication","Plastics","Energy","Other"
];

export const AUDIENCE_TYPES = [
  "Facility / Company",
  "Environmental Consultant",
  "EHS Consultant",
  "Engineering / Compliance Firm",
  "Other",
];

export const TIER2_FAQ = [
  { q: "What is Tier II reporting?", a: "Tier II is an annual chemical inventory report under EPCRA Section 312. Facilities that store hazardous chemicals at or above applicable thresholds report what they store, how much, where, and the associated hazards to state and local authorities." },
  { q: "Who typically needs to submit Tier II information?", a: "Facilities that store hazardous chemicals — for which an SDS is required — at or above threshold quantities during the year. Extremely Hazardous Substances have much lower thresholds. Applicability depends on your specific inventory and location." },
  { q: "What information is needed to prepare a Tier II report?", a: "Typically a list of on-site hazardous chemicals, current SDS, maximum and average quantities, storage locations and conditions, and container/hazard details. We help you assemble and structure this." },
  { q: "Can you support multiple facilities?", a: "Yes. Our workflows are built to handle single sites or multi-facility portfolios with consistent structure across locations." },
  { q: "Do reporting requirements vary by state?", a: "Yes. Portals, fees, deadlines and additional data fields differ by state and locality. Requirements and procedures vary by state and facility — confirm applicable requirements with the relevant state/local authorities." },
  { q: "Can consultants outsource Tier II preparation?", a: "Yes. We provide back-office and white-label preparation for environmental, EHS, engineering and compliance firms, so your firm keeps the client relationship." },
  { q: "How does the review process work?", a: "Deliverables move through structured preparation and a separate internal QA/technical review step before we hand them to you for your own review and finalization." },
  { q: "How do we get started?", a: "Submit the free assessment or request a quote with a few details about your facility and chemicals. We'll confirm scope, collect documents securely, and outline next steps." },
];

// State-specific Tier II pages. Only published states with genuinely useful content appear.
export const STATE_PAGES = [
  {
    slug: "texas",
    state: "Texas",
    agency: "Texas Commission on Environmental Quality (TCEQ)",
    intro: "Texas administers Tier II reporting through the Texas Commission on Environmental Quality (TCEQ). Facilities storing hazardous chemicals above EPCRA thresholds must file annually.",
    points: [
      "Texas uses its own State of Texas Environmental Electronic Reporting System (STEERS) for Tier II submissions.",
      "Reports are generally due by March 1 for the prior calendar year.",
      "Facilities must also coordinate with their LEPC and local fire department.",
      "Filing fees may apply based on the number and type of chemicals reported.",
    ],
  },
  {
    slug: "california",
    state: "California",
    agency: "California Environmental Reporting System (CERS)",
    intro: "California integrates Tier II reporting into its Hazardous Materials Business Plan (HMBP) program through the California Environmental Reporting System (CERS).",
    points: [
      "California facilities typically report chemical inventories via CERS as part of the HMBP.",
      "Local Certified Unified Program Agencies (CUPAs) administer the program locally.",
      "Thresholds and additional local requirements may differ from federal baselines.",
      "Annual review and updates are expected to keep the HMBP current.",
    ],
  },
  {
    slug: "florida",
    state: "Florida",
    agency: "Florida Division of Emergency Management (FDEM)",
    intro: "Florida administers EPCRA Tier II reporting through the Florida Division of Emergency Management using the E-Plan system.",
    points: [
      "Florida facilities commonly submit Tier II data through E-Plan.",
      "Reports are generally due by March 1 each year.",
      "Coordination with the LEPC and local fire department is required.",
      "Verify current state fees and portal requirements before filing.",
    ],
  },
  {
    slug: "ohio",
    state: "Ohio",
    agency: "State Emergency Response Commission (SERC) of Ohio",
    intro: "Ohio administers Tier II reporting through the Ohio SERC, with submissions typically made via the state's designated reporting system.",
    points: [
      "Ohio facilities file annual Tier II inventory reports with the SERC, LEPC and fire department.",
      "Reports are generally due by March 1 for the previous calendar year.",
      "State-specific fees and forms may apply.",
      "Maintain documentation to support audits and inspections.",
    ],
  },
  {
    slug: "michigan",
    state: "Michigan",
    agency: "Michigan Department of Environment, Great Lakes, and Energy (EGLE)",
    intro: "Michigan administers Tier II reporting in coordination with EGLE and the Michigan SERC.",
    points: [
      "Michigan facilities submit annual Tier II inventory reports to the SERC, LEPC and fire department.",
      "Reports are generally due by March 1 each year.",
      "Confirm the current submission portal and any state fees before filing.",
      "Keep supporting SDS and calculations on file for audits.",
    ],
  },
];
